/**
 * LoadFromVaultBanner.jsx — iter906 (Feb 2026)
 *
 * Shared banner used by MindBodyLabPage + NutritionLabPage to import the
 * user's active conditions from the Pathway Vault (single source of
 * truth). Eliminates duplicate menus: once you have a vault, both labs
 * can auto-align to it with one click.
 *
 * Behaviour:
 *   - Fetches GET /api/pathway-vault/driving-conditions on mount
 *   - Renders nothing if the vault is empty (no active conditions)
 *   - Otherwise shows: "N състояния · M пътища активни във Vault · [Load]"
 *   - Clicking "Load" calls onApply(all_conditions[]) → parent auto-fills
 *     its state
 *   - When the parent already matches, the button becomes "Aligned ✓"
 *
 * Props:
 *   currentConditions : string[]     current condition IDs in parent state
 *   onApply           : (ids) => {}  called with the vault conditions
 *   isDark            : boolean
 *   testid            : string       optional data-testid override
 *   lang              : "bg" | "en"
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Database, CheckCircle2, ArrowRight, Loader2, Info } from "lucide-react";
import { useAuth } from "@/App";

const COPY = {
  bg: {
    title:       "Твоят Pathway Vault",
    subtitle_a:  "състояния и",
    subtitle_b:  "KEGG пътища активни",
    subtitle_lab_a: "включително",
    subtitle_lab_b: "от лабораторни резултати",
    load:        "Зареди във филтрите",
    loading:     "Зарежда…",
    aligned:     "Синхронизиран с Vault",
    empty:       "",
    error:       "Vault не е достъпен",
    tooltip:     "Vault агрегира всичките ти условия/диагнози и авто-derived сигнали от лабораторни. Ако го синхронизираш тук, ще получиш еднакви препоръки навсякъде.",
  },
  en: {
    title:       "Your Pathway Vault",
    subtitle_a:  "conditions and",
    subtitle_b:  "KEGG pathways active",
    subtitle_lab_a: "including",
    subtitle_lab_b: "from lab results",
    load:        "Load into filters",
    loading:     "Loading…",
    aligned:     "Synced with Vault",
    empty:       "",
    error:       "Vault unavailable",
    tooltip:     "The Vault aggregates all your conditions/diagnoses plus auto-derived lab signals. Syncing here means both Labs give the same recommendations.",
  },
};

export default function LoadFromVaultBanner({
  currentConditions = [],
  onApply,
  isDark = false,
  lang = "bg",
  testid = "load-from-vault-banner",
}) {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const t = COPY[lang] || COPY.bg;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/pathway-vault/driving-conditions`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API]);

  const vaultIds = useMemo(() => (data?.all_conditions || []), [data]);
  const isAligned = useMemo(() => {
    if (!vaultIds.length) return false;
    const cur = new Set(currentConditions || []);
    return vaultIds.length > 0
      && vaultIds.every((id) => cur.has(id))
      && vaultIds.length === (currentConditions || []).length;
  }, [vaultIds, currentConditions]);

  if (loading) {
    return (
      <div
        data-testid={testid}
        className={`rounded-2xl border p-3 flex items-center gap-2
          ${isDark ? "bg-slate-900/60 border-slate-700" : "bg-slate-50 border-slate-200"}`}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin opacity-70" />
        <span className="text-[11px] opacity-70">{t.loading}</span>
      </div>
    );
  }

  // Hide the banner entirely if the vault is empty (no conditions).
  if (!data || !vaultIds.length) return null;

  const labCount = (data.lab_derived_conditions || []).length;
  const pathwayCount = data.pathway_count || (data.pathway_ids || []).length || 0;

  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-3 flex items-start gap-3
        ${isDark
          ? "bg-gradient-to-br from-emerald-900/25 to-sky-900/20 border-emerald-700/40"
          : "bg-gradient-to-br from-emerald-50 to-sky-50 border-emerald-200"}
      `}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0
          ${isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
        <Database className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
            {t.title}
          </span>
          <span
            title={t.tooltip}
            className={`inline-flex items-center ${isDark ? "text-slate-400" : "text-slate-500"} cursor-help`}
          >
            <Info className="w-3 h-3" />
          </span>
        </div>
        <div className={`text-[11px] mt-0.5 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          <b>{vaultIds.length}</b> {t.subtitle_a} <b>{pathwayCount}</b> {t.subtitle_b}
          {labCount > 0 && (
            <span className={`ml-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
              · {t.subtitle_lab_a} <b>{labCount}</b> {t.subtitle_lab_b}
            </span>
          )}
        </div>
      </div>

      {isAligned ? (
        <span
          data-testid={`${testid}-aligned`}
          className={`shrink-0 inline-flex items-center gap-1 text-[10.5px] px-2 py-1 rounded-full font-semibold
            ${isDark ? "bg-emerald-500/25 text-emerald-200" : "bg-emerald-100 text-emerald-800"}`}
        >
          <CheckCircle2 className="w-3 h-3" /> {t.aligned}
        </span>
      ) : (
        <button
          data-testid={`${testid}-apply`}
          onClick={() => onApply?.(vaultIds)}
          className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white
            bg-gradient-to-r from-emerald-500 to-sky-500 hover:opacity-90`}
        >
          {t.load} <ArrowRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
